import pdfMake from 'pdfmake/build/pdfmake.min';
import vfsFonts from 'pdfmake/build/vfs_fonts';
import moment from "moment";
import { decodeToken, IsNullOrEmpty, maskEmail, maskMobile } from './utility';
import _ from "underscore"
require('dotenv').config();

const PAGE_MARGIN = 32;
const EXPLORER_URL = 'https://bscscan.com/tx/';

// Payout rows carry the member document through a $lookup; it can be empty when
// the member was removed, so never index into it blindly.
const memberOf = (item) => (item && item.members && item.members[0]) || {};

const fullName = (person) => `${person.firstName || ""} ${person.lastName || ""}`.trim() || "—";

const value = (input) => (IsNullOrEmpty(input) ? "—" : input);

/** Trims floating point noise and groups thousands for readability. */
const round = (input) => {
    const parsed = parseFloat(input);
    if (!isFinite(parsed)) return "0";
    return (Math.round(parsed * 1000) / 1000)
        .toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 3 });
};

/** Compact date formats keep the columns narrow enough to fit the page. */
const shortDate = (input) => (input ? moment(input).format("DD MMM YY") : "—");
const shortDateTime = (input) => (input ? moment(input).format("DD MMM YY, HH:mm") : "—");

const money = (input, suffix = "") => ({
    text: IsNullOrEmpty(input) ? "—" : `${input}${suffix ? " " + suffix : ""}`,
    alignment: "right",
    noWrap: true,
});

const explorerLink = (label, hash) => (IsNullOrEmpty(hash)
    ? null
    : { text: label, link: EXPLORER_URL + hash, decoration: "underline", color: "#1a4f8a" });

/** Collapses several transaction hashes into a single narrow column of links. */
const hashCell = (links) => {
    const available = links.filter(Boolean);
    if (available.length === 0) return { text: "—" };
    const parts = [];
    available.forEach((link, index) => {
        if (index > 0) parts.push(" · ");
        parts.push(link);
    });
    return { text: parts, noWrap: true };
};

export function generatePdf(data, title, text, pageSize = "A4", pageOrientation = "landscape", widths = null) {
    let currentUser = decodeToken();
    const columnCount = (data[0] || []).length;
    const body = data.map((row, index) => (index === 0
        ? row.map((cell) => ({
            text: cell && typeof cell === "object" ? cell.text : cell,
            style: 'tableHeader',
        }))
        : row));

    var docDefinition = {
        pageSize: pageSize,
        pageOrientation: pageOrientation,
        pageMargins: [PAGE_MARGIN, 64, PAGE_MARGIN, 42],
        defaultStyle: {
            fontSize: 8.5,
            lineHeight: 1.1,
        },
        footer: function (currentPage, pageCount, size) {
            const width = (size && size.width) || 842;
            return {
                margin: [PAGE_MARGIN, 8, PAGE_MARGIN, 0],
                stack: [
                    { canvas: [{ type: 'line', x1: 0, y1: 0, x2: width - (PAGE_MARGIN * 2), y2: 0, lineWidth: 0.5, lineColor: '#bbbbbb' }] },
                    {
                        columns: [
                            { text: `Generated ${moment().format("DD MMM YY, HH:mm")} by ${currentUser.email}`, style: 'footerText' },
                            { text: `Page ${currentPage} of ${pageCount}`, style: 'footer' },
                        ],
                        margin: [0, 6, 0, 0],
                    },
                ],
            };
        },
        header: function (currentPage, pageCount, size) {
            const width = (size && size.width) || 842;
            return {
                margin: [PAGE_MARGIN, 20, PAGE_MARGIN, 0],
                stack: [
                    { text: title, style: 'header' },
                    { canvas: [{ type: 'line', x1: 0, y1: 6, x2: width - (PAGE_MARGIN * 2), y2: 6, lineWidth: 1.2, lineColor: '#333333' }] },
                ],
            };
        },
        watermark: { text: "Team369 Confidential", opacity: 0.09, bold: true, italics: false, fontSize: 46 },
        content: [
            IsNullOrEmpty(text) ? "" : { text: text, style: 'subtitle' },
            {
                table: {
                    headerRows: 1,
                    dontBreakRows: true,
                    widths: widths && widths.length === columnCount
                        ? widths
                        : new Array(columnCount).fill('*'),
                    body: body,
                },
                layout: {
                    hLineWidth: function (i, node) {
                        return (i === 0 || i === 1 || i === node.table.body.length) ? 1 : 0.5;
                    },
                    vLineWidth: function () {
                        return 0;
                    },
                    hLineColor: function (i, node) {
                        return (i === 0 || i === 1 || i === node.table.body.length) ? '#8a8a8a' : '#dddddd';
                    },
                    paddingLeft: function (i) { return i === 0 ? 2 : 5; },
                    paddingRight: function () { return 5; },
                    paddingTop: function () { return 5; },
                    paddingBottom: function () { return 5; },
                    fillColor: function (rowIndex) {
                        if (rowIndex === 0) return '#e6ebf0';
                        return rowIndex % 2 === 0 ? '#f6f8fa' : null;
                    }
                }
            }
        ],
        styles: {
            header: {
                fontSize: 15,
                bold: true,
                alignment: "center",
            },
            subtitle: {
                fontSize: 9,
                italics: true,
                color: '#555555',
                alignment: "center",
                margin: [0, 0, 0, 8]
            },
            tableHeader: {
                fontSize: 8,
                bold: true,
                color: '#25303b',
            },
            footerText: {
                fontSize: 7.5,
                italics: true,
                color: '#666666',
            },
            footer: {
                fontSize: 7.5,
                italics: true,
                color: '#666666',
                alignment: "right",
            },
        }
    };

    const { vfs } = vfsFonts.pdfMake;
    pdfMake.vfs = vfs;
    pdfMake.createPdf(docDefinition)
        .download(`${title.replace(/\s+/g, "_")}_${moment().format("YYYYMMDD_HHmm")}`);
};

export function downloadIllustrationReport(items) {
    let data = [];
    data.push([
        "S.No",
        "Date",
        "Deposit",
        "Reward",
        "Net Withdraw",
        "Gas Fees",
        "Liquidity",
        "Reinvestment",
    ]);
    let cnt = 0;
    items.forEach((item) => {
        cnt = cnt + 1;
        data.push([
            { text: cnt },
            { text: item.date, noWrap: true },
            money(item.amount),
            money(item.interest),
            money(item.withdrawalAmount),
            money(item.adminFees),
            money(item.liquidityFees),
            money(item.reinvest),
        ]);
    });

    generatePdf(data, "Plan Illustration", "", "A4", "portrait",
        [26, 62, '*', '*', '*', '*', '*', '*']);
};

export function downloadReferralReport(referals, filterText = "") {
    referals = _.sortBy(referals, "depth");
    let data = [];
    data.push([
        'S.No',
        'Level',
        'Member ID',
        'Member Name',
        'Email',
        'Mobile',
        'Referred By',
    ]);
    let cnt = 0;

    referals.forEach((item) => {
        cnt = cnt + 1;
        data.push([
            { text: cnt },
            { text: parseInt(item.depth) + 1, alignment: "center" },
            { text: value(item.memberId), noWrap: true },
            { text: fullName(item) },
            { text: maskEmail(item.email) },
            { text: maskMobile(item.mobileNumber), noWrap: true },
            { text: value(item.referal), noWrap: true },
        ]);
    });

    generatePdf(data, "Referral Report", filterText, "A4", "landscape",
        [26, 38, 78, '*', '*', 82, 78]);
};

export function downloadSwapReport(deposits, type, filterText = "") {
    let data = [];
    data.push([
        'S.No',
        'Member ID',
        'Date',
        'Reference No',
        'Coins',
        'Tokens',
        'Fees',
        'Transaction',
    ]);
    let cnt = 0;

    deposits.forEach((item) => {
        cnt = cnt + 1;
        data.push([
            { text: cnt },
            { text: value(item.createdBy), noWrap: true },
            { text: shortDateTime(item.createdAt), noWrap: true },
            { text: value(item.referenceNo) },
            money(item.transactionCoins, item.coinType),
            money(item.transactionTokens, item.tokenSymbol),
            money(item.transactionCharge, item.coinType),
            hashCell([
                explorerLink("Spent", item.spentHash),
                explorerLink("Recv", item.receiveHash),
                explorerLink("Fee", item.transactionChargeHash),
            ]),
        ]);
    });

    generatePdf(data, type, filterText, "A4", "landscape",
        [26, 78, 88, '*', 92, 92, 82, 96]);
};

export function downloadWithdrawReport(withdraws, filterText = "") {
    let data = [];
    data.push([
        'S.No',
        'Member ID',
        'Date',
        'Reference No',
        'Amount',
        'Status',
    ]);
    let cnt = 0;

    withdraws.forEach((item) => {
        cnt = cnt + 1;
        data.push([
            { text: cnt },
            { text: value(item.createdBy), noWrap: true },
            { text: shortDateTime(item.createdAt), noWrap: true },
            { text: value(item.referenceNo) },
            money(item.amount, item.isToken ? item.tokenSymbol : item.coinType),
            { text: value(item.status), noWrap: true },
        ]);
    });

    generatePdf(data, "Withdraw Report", filterText, "A4", "portrait",
        [26, 72, 88, '*', 92, 58]);
};

export function downloadRewardReport(referals, filterText = "") {
    referals = referals.filter((t) => t.method === "Reward");
    referals = _.sortBy(referals, "memberId");
    let data = [];
    data.push([
        'S.No',
        'Member ID',
        'Member Name',
        'Date',
        'Type',
        'Reward',
        'Status',
    ]);
    let cnt = 0;

    referals.forEach((item) => {
        cnt = cnt + 1;
        data.push([
            { text: cnt },
            { text: value(item.memberId), noWrap: true },
            { text: fullName(memberOf(item)) },
            { text: shortDateTime(item.createdAt), noWrap: true },
            { text: value(item.type) },
            money(item.amount, "USD"),
            { text: value(item.status), noWrap: true },
        ]);
    });

    generatePdf(data, "Reward Report", filterText, "A4", "landscape",
        [26, 78, '*', 88, '*', 82, 62]);
};

export function downloadRefReport(referals, filterText = "") {
    referals = referals.filter((t) => t.method === "Referral");
    referals = _.sortBy(referals, "memberId");
    let data = [];
    data.push([
        'S.No',
        'From Member ID',
        'Member ID',
        'Member Name',
        'Date',
        'Commission',
        'Status',
    ]);
    let cnt = 0;

    referals.forEach((item) => {
        cnt = cnt + 1;
        data.push([
            { text: cnt },
            { text: value(item.createdBy), noWrap: true },
            { text: value(item.memberId), noWrap: true },
            { text: fullName(memberOf(item)) },
            { text: shortDateTime(item.createdAt), noWrap: true },
            money(item.amount, "USD"),
            { text: value(item.status), noWrap: true },
        ]);
    });

    generatePdf(data, "Referral Commission Report", filterText, "A4", "landscape",
        [26, 88, 78, '*', 88, 82, 62]);
};

export function downloadReTopReport(referals, filterText = "") {
    referals = _.sortBy(referals, "memberId");
    let data = [];
    data.push([
        'S.No',
        'Member ID',
        'Member Name',
        'Date',
        'Type',
        'Reinvestment',
        'Status',
    ]);
    let cnt = 0;

    referals.forEach((item) => {
        cnt = cnt + 1;
        data.push([
            { text: cnt },
            { text: value(item.memberId), noWrap: true },
            { text: fullName(memberOf(item)) },
            { text: shortDateTime(item.createdAt), noWrap: true },
            { text: value(item.type) },
            money(item.reinvestment, "USD"),
            { text: value(item.status), noWrap: true },
        ]);
    });

    generatePdf(data, "Retopup Report", filterText, "A4", "landscape",
        [26, 78, '*', 88, '*', 82, 62]);
};

export function downloadReTopCommissionReport(withdraws, filterText = "") {
    let data = [];
    data.push([
        'S.No',
        'Member ID',
        'Plan Start',
        'Date',
        'Type',
        'Reinvestment',
        'Status',
    ]);
    let cnt = 0;

    withdraws.forEach((item) => {
        cnt = cnt + 1;
        const planStart = (item.planMembers && item.planMembers[0] && item.planMembers[0].createdAt) || item.createdAt;
        data.push([
            { text: cnt },
            { text: value(item.memberId), noWrap: true },
            { text: shortDate(planStart), noWrap: true },
            { text: shortDateTime(item.createdAt), noWrap: true },
            { text: value(item.type) },
            money(item.reinvestment, "USD"),
            { text: value(item.status), noWrap: true },
        ]);
    });

    generatePdf(data, "Retopup Commission Report", filterText, "A4", "landscape",
        [26, 78, 72, 88, '*', 82, 62]);
};

export function downloadActiveIDReport(referals, filterText = "") {
    referals = _.filter(referals, s => s.planMember && s.planMember.length > 0);
    let data = [];
    data.push([
        'S.No',
        'Member ID',
        'Member Name',
        'Email',
        'Mobile',
        'Referred By',
        'Joined On',
        'Activated On',
    ]);
    let cnt = 0;

    referals.forEach((item) => {
        cnt = cnt + 1;
        const plans = item.planMember || [];
        const activated = plans.length > 0
            ? _.sortBy(plans, t => moment(t.createdAt).unix())[0].createdAt
            : null;

        data.push([
            { text: cnt },
            { text: value(item.memberId), noWrap: true },
            { text: fullName(item) },
            { text: maskEmail(item.email) },
            { text: maskMobile(item.mobileNumber), noWrap: true },
            { text: value(item.referal), noWrap: true },
            { text: shortDate(item.createdAt), noWrap: true },
            { text: shortDate(activated), noWrap: true },
        ]);
    });

    generatePdf(data, "Active ID Report", filterText, "A4", "landscape",
        [26, 78, '*', '*', 82, 78, 68, 72]);
};

export function downloadInActiveIDReport(referals, filterText = "") {
    referals = _.filter(referals, s => !s.planMember || s.planMember.length === 0);
    let data = [];
    data.push([
        'S.No',
        'Member ID',
        'Member Name',
        'Email',
        'Mobile',
        'Referred By',
        'Joined On',
        'Verified',
    ]);
    let cnt = 0;

    referals.forEach((item) => {
        cnt = cnt + 1;
        const verified = [
            item.isEmailVerified ? "Email" : null,
            item.isMobileVerified ? "Mobile" : null,
            item.walletAddress ? "Wallet" : null,
        ].filter(Boolean);

        data.push([
            { text: cnt },
            { text: value(item.memberId), noWrap: true },
            { text: fullName(item) },
            { text: maskEmail(item.email) },
            { text: maskMobile(item.mobileNumber), noWrap: true },
            { text: value(item.referal), noWrap: true },
            { text: shortDate(item.createdAt), noWrap: true },
            { text: verified.length ? verified.join(", ") : "None" },
        ]);
    });

    generatePdf(data, "In Active ID Report", filterText, "A4", "landscape",
        [26, 78, '*', '*', 82, 78, 68, 88]);
};

/** Members owed at or below this are not worth settling yet, so they are excluded. */
export const PENDING_PAYOUT_MINIMUM = 50;

/**
 * Pending payouts (status "Created") rolled up per member: how many payouts are
 * queued, which methods produced them, and how much is owed in total. Only
 * members owed more than PENDING_PAYOUT_MINIMUM are listed, highest first.
 */
export function downloadPendingPayoutReport(payouts, filterText = "") {
    const grouped = _.groupBy(payouts, (item) => item.memberId || "—");
    const perMember = Object.keys(grouped).map((memberId) => {
        const items = grouped[memberId];
        return {
            memberId: memberId,
            name: fullName(memberOf(items[0])),
            count: items.length,
            methods: _.uniq(items.map((i) => i.method).filter(Boolean)).join(", ") || "—",
            oldest: _.min(items.map((i) => moment(i.createdAt).valueOf())),
            total: items.reduce((sum, i) => sum + (parseFloat(i.amount) || 0), 0),
        };
    });

    const ranked = _.sortBy(perMember, (row) => -row.total);
    const rows = ranked.filter((row) => row.total > PENDING_PAYOUT_MINIMUM);
    const skipped = ranked.length - rows.length;
    const shownPayouts = rows.reduce((sum, row) => sum + row.count, 0);
    const grandTotal = rows.reduce((sum, row) => sum + row.total, 0);

    let data = [];
    data.push([
        'S.No',
        'Member ID',
        'Member Name',
        'Payouts',
        'Methods',
        'Pending Since',
        'Amount',
    ]);

    rows.forEach((row, index) => {
        data.push([
            { text: index + 1 },
            { text: value(row.memberId), noWrap: true },
            { text: row.name },
            { text: row.count, alignment: "right" },
            { text: row.methods },
            { text: shortDate(row.oldest), noWrap: true },
            money(round(row.total), "USD"),
        ]);
    });

    if (rows.length > 0) {
        data.push([
            { text: `TOTAL — ${rows.length} members`, colSpan: 3, bold: true },
            {},
            {},
            { text: shownPayouts, bold: true, alignment: "right" },
            {},
            {},
            { text: `${round(grandTotal)} USD`, bold: true, alignment: "right", noWrap: true },
        ]);
    } else {
        data.push([
            {
                text: `No member has more than ${PENDING_PAYOUT_MINIMUM} USD pending.`,
                colSpan: 7,
                alignment: "center",
                italics: true,
            },
            {}, {}, {}, {}, {}, {},
        ]);
    }

    const headline = [
        `${shownPayouts} pending payouts  ·  ${rows.length} members  ·  ${round(grandTotal)} USD awaiting settlement`,
        `Only members owed more than ${PENDING_PAYOUT_MINIMUM} USD are listed${skipped > 0 ? ` — ${skipped} below the threshold excluded` : ""}`,
    ].join("\n");

    generatePdf(data, "Payout Pending Report",
        IsNullOrEmpty(filterText) ? headline : `${headline}\n${filterText}`,
        "A4", "landscape",
        [26, 78, '*', 52, '*', 78, 92]);
};

export function downloadPayoutReport(referals, filterText = "") {
    referals = _.sortBy(referals, "memberId");
    let data = [];
    data.push([
        'S.No',
        'Member ID',
        'Member Name',
        'Date',
        'Method',
        'Type',
        'Amount',
        'Status',
    ]);
    let cnt = 0;

    referals.forEach((item) => {
        cnt = cnt + 1;
        data.push([
            { text: cnt },
            { text: value(item.memberId), noWrap: true },
            { text: fullName(memberOf(item)) },
            { text: shortDateTime(item.createdAt), noWrap: true },
            { text: value(item.method), noWrap: true },
            { text: value(item.type) },
            money(item.amount, "USD"),
            { text: value(item.status), noWrap: true },
        ]);
    });

    generatePdf(data, "Payout Report", filterText, "A4", "landscape",
        [26, 78, '*', 88, 62, '*', 82, 62]);
};
